import React, { createContext, PropsWithChildren, useContext, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import Banner, { IUBannerConfig } from 'Common/components/Banner';

interface IUNotificationContext {
  banners: IUBannerConfig[];

  showBanner: (config: IUBannerConfig) => void;
  hideBanner: (configId: string) => void;
}

type IUBannerConfigInternal = IUBannerConfig & {
  id: string;
}

const NotificationContext = createContext<IUNotificationContext | null>(null);

export function NotificationContextProvider(props: PropsWithChildren<object>) {
  const [banners, setBanners] = useState<IUBannerConfigInternal[]>([]);

  const showBanner = (config: IUBannerConfig) => {
    const id = config.id ?? uuidv4();

    setBanners([
      ...banners,
      {
        ...config,
        id
      }
    ]);

    if (config.duration) {
      setTimeout(() => hideBanner(id), config.duration);
    }

    return id;
  };

  const hideBanner = (configId: string) => {
    setBanners((oldBanners) => {
      return oldBanners.filter(banner => banner.id !== configId);
    });
  };

  const context: IUNotificationContext = {
    banners,

    showBanner,
    hideBanner
  };

  return <NotificationContext.Provider value={context}>
    {props.children}
  </NotificationContext.Provider>;
}

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (context === null) {
    throw new Error('useNotificationContext must be used within a NotificationContextProvider');
  }
  return context;
};

export const NotificationOutlet = () => {
  const notificationContext = useNotificationContext();

  return <>
    {
      notificationContext.banners.map(banner => <Banner
        key={banner.id}
        config={banner}
        onClose={() => notificationContext.hideBanner(banner.id ?? '')}
      />)
    }
  </>;
};
